import React, { FC, useCallback, useEffect, useMemo, SyntheticEvent } from "react";
import _ from "lodash";
import { $, merge } from "glamor";
import { theme } from "styles/theme";
import ColorSwatch from "components/color-swatch";
import { DATATEST } from "constants/integration";
import { IItemRecord, IVariant } from "records/item/item";

const MAX_CHIPS = 5;

interface IColorChipsProps {
    item: IItemRecord;
    variants: IVariant[];
    onSwatchSelected(e: SyntheticEvent, product: { _id: string; name: string }): void;
    isGrapesJs?: boolean;
    onViewMore(e: SyntheticEvent): void;
    maxChipsAmount?: number;
}

export const ColorChips: FC<IColorChipsProps> = ({
    item,
    variants,
    maxChipsAmount = MAX_CHIPS,
    onSwatchSelected,
    isGrapesJs,
    onViewMore,
}) => {
    const [selected, setSelected] = React.useState<string>();

    useEffect(() => {
        setSelected(_.find(variants, { ref: item._id })?.ref);
    }, [item, variants]);

    const handleSwatchSelected = useCallback(
        (e: SyntheticEvent, { _id, name }: IItemRecord) => {
            onSwatchSelected(e, { _id, name });
            setSelected(_id);
        },
        [onSwatchSelected],
    );

    const slides = useMemo(
        () => {
            return _.map(
                _.sortBy(variants, ({ ref }) => ref !== selected), // set selected variant to the first position
                (variant) => (
                    <ColorSwatch
                        key={variant.ref}
                        item={{ _id: variant.ref, name: variant.swatch_label || variant.display_name }}
                        onClick={handleSwatchSelected}
                        swatch={variant.color_swatch}
                        selected={variant.ref === selected}
                        tooltip={variant.swatch_label || variant.display_name}
                        isGrapesJs={isGrapesJs}
                    />
                ),
            );
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [variants, handleSwatchSelected, selected],
    );

    const styles = useClassStyles();
    return (
        <div {...styles} data-test={DATATEST.GALLERY.COLOR_CHIPS}>
            {_.size(slides) <= maxChipsAmount && <div>{slides}</div>}
            {_.size(slides) > maxChipsAmount && (
                <div>
                    {_.slice(slides, 0, maxChipsAmount - 1)}
                    <div
                        data-test={DATATEST.GALLERY.MORE_COLORS_COUNT}
                        key="number"
                        className="moreColorsCount"
                        onClick={onViewMore}
                    >
                        <div>{`+${_.size(slides) - maxChipsAmount + 1}`}</div>
                    </div>
                </div>
            )}
        </div>
    );
};

const useClassStyles = () =>
    useMemo(() => {
        return merge(
            {
                position: "absolute",
                bottom: "4px",
            },
            $(" .swatchContainer", {
                width: "32px !important",
            }),
            $(" .swatchContainer.selected", {
                border: `2px solid ${theme.palette.accent1Color} !important`,
            }),
            $(" .moreColorsCount", {
                display: "inline-block",
                border: "2px solid transparent",
                position: "relative",
                height: "32px",
                width: "32px",
                marginRight: "0px",
            }),
            $(" .moreColorsCount > div", {
                display: "block",
                position: "absolute",
                top: "2px",
                left: "2px",
                height: "24px",
                width: "24px",
                fontSize: "14px",
                paddingTop: "2px",
            }),
        );
    }, []);

export default ColorChips;
